// AUPRÉA — Liste des parcours (brouillons + réalisés) + rendez-vous à venir
import { useState, useEffect } from "react";
import { C, Btn, Input, Select, Alert, Spinner, FormField, Modal, parcoursApi, rdvApi, DateInput, fdate, errMsg, BESOIN_TYPE } from "./shared";
import { LeadPicker } from "./LeadPicker";
import { PARCOURS_CATALOG } from "./parcoursQuestionnaires";
import { SuiviModal } from "./SuiviModal";

const fmtDate = fdate;

const waLink = (r, user) => {
  const DOM = { "0690": "590", "0691": "590", "0590": "590", "0696": "596", "0697": "596", "0596": "596", "0694": "594", "0594": "594", "0692": "262", "0693": "262", "0262": "262", "0639": "269", "0269": "269" };
  let tel = (r.client_telephone || "").replace(/[^\d+]/g, "").replace(/^00/, "+");
  tel = tel.startsWith("+") ? tel.slice(1) : (tel.length === 10 && tel[0] === "0" ? (DOM[tel.slice(0, 4)] || "33") + tel.slice(1) : tel);
  const lieu = r.format === "visio" ? `en visio${r.lien_visio ? ` : ${r.lien_visio}` : ""}` : `en présentiel${r.lieu ? `, ${r.lieu}` : ""}`;
  const txt = `Bonjour ${r.client_prenom || r.client_nom}, je vous confirme notre rendez-vous AUPRÉA le ${fdate(r.date)}${r.heure ? ` à ${r.heure}` : ""}, ${lieu}. ${user.nom}${user.tel ? ` — ${user.tel}` : ""}`;
  return `https://wa.me/${tel}?text=${encodeURIComponent(txt)}`;
};

const typeInfo = (code) => PARCOURS_CATALOG.find(p => p.code === code) || { titre: code, couleur: C.mid };

// ── Modale planifier RDV ────────────────────────────────────────────────────
const RdvModal = ({ user, initial, initialLead, onClose, onSaved }) => {
  const [form, setForm] = useState(initial || {
    conseillerId: user.conseiller_id || user.id,
    client_nom: "", client_prenom: "", client_email: "", client_telephone: "",
    date: "", heure: "", type_parcours: "bilan_succession", notes: "", statut: "planifie",
    format: "presentiel", lieu: "", lien_visio: "", leadId: null, source: "",
  });
  const [lead, setLead] = useState(initialLead || null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const pick = (l, base = form) => {
    setLead(l);
    if (!l) return setForm({ ...base, leadId: null });
    const i = l.identite || {};
    setForm({ ...base, leadId: l.id, client_nom: i.nom || "", client_prenom: i.prenom || "", client_email: i.email || "", client_telephone: i.telephone || "", type_parcours: BESOIN_TYPE[l.besoin] || base.type_parcours });
  };
  useEffect(() => { if (initialLead) pick(initialLead); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => {
    if (!form.client_nom || !form.date) return setErr("Nom du client et date requis");
    if (!form.id && !form.leadId && !form.source) return setErr("Choisissez un lead ou indiquez la source du prospect");
    setSaving(true); setErr("");
    try {
      if (form.id) await rdvApi.update(form.id, form); else await rdvApi.create(form);
      onSaved(); onClose();
    } catch (e) { setErr(errMsg(e)); }
    finally { setSaving(false); }
  };
  return (
    <Modal title={form.id ? "Modifier le rendez-vous" : "Nouveau rendez-vous"} onClose={onClose} wide
      footer={<><Btn onClick={onClose}>Annuler</Btn><Btn variant="gold" disabled={saving} onClick={save} data-testid="rdv-save">{saving ? "…" : "Enregistrer"}</Btn></>}>
      {err && <Alert type="error"><span>⚠</span><span data-testid="rdv-error">{err}</span></Alert>}
      <LeadPicker lead={lead} onPick={(l) => pick(l)} source={form.source} onSource={(v) => set("source", v)} locked={!!form.id && !!form.leadId} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <FormField label="Nom" required><Input value={form.client_nom} onChange={(e) => set("client_nom", e.target.value)} data-testid="rdv-nom" /></FormField>
        <FormField label="Prénom"><Input value={form.client_prenom} onChange={(e) => set("client_prenom", e.target.value)} data-testid="rdv-prenom" /></FormField>
        <FormField label="Email"><Input type="email" value={form.client_email} onChange={(e) => set("client_email", e.target.value)} data-testid="rdv-email" /></FormField>
        <FormField label="Téléphone"><Input value={form.client_telephone} onChange={(e) => set("client_telephone", e.target.value)} data-testid="rdv-tel" /></FormField>
        <FormField label="Date" required><DateInput value={form.date} onChange={(v) => set("date", v)} data-testid="rdv-date" /></FormField>
        <FormField label="Heure"><Input type="time" value={form.heure} onChange={(e) => set("heure", e.target.value)} data-testid="rdv-heure" /></FormField>
        <FormField label="Format">
          <Select value={form.format || "presentiel"} onChange={(e) => set("format", e.target.value)} data-testid="rdv-format">
            <option value="presentiel">Présentiel</option><option value="visio">Visio</option>
          </Select>
        </FormField>
        {form.format === "visio"
          ? <FormField label="Lien visio"><Input value={form.lien_visio || ""} onChange={(e) => set("lien_visio", e.target.value)} placeholder="https://…" data-testid="rdv-lien" /></FormField>
          : <FormField label="Lieu"><Input value={form.lieu || ""} onChange={(e) => set("lieu", e.target.value)} placeholder="Adresse du rendez-vous" data-testid="rdv-lieu" /></FormField>}
        <FormField label="Parcours prévu" required>
          <Select value={form.type_parcours} onChange={(e) => set("type_parcours", e.target.value)} data-testid="rdv-type">
            {PARCOURS_CATALOG.map(p => <option key={p.code} value={p.code}>{p.titre}</option>)}
          </Select>
        </FormField>
        <FormField label="Statut">
          <Select value={form.statut} onChange={(e) => set("statut", e.target.value)} data-testid="rdv-statut">
            <option value="planifie">Planifié</option>
            <option value="realise">Réalisé</option>
            <option value="annule">Annulé</option>
          </Select>
        </FormField>
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 10 }}>Le prospect reçoit une confirmation par e-mail, puis un rappel la veille.</div>
    </Modal>
  );
};

// ── Page principale ─────────────────────────────────────────────────────────
export const ParcoursList = ({ user, onOpenParcours, onNouveau }) => {
  const [parcours, setParcours] = useState([]);
  const [rdvs, setRdvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("brouillons"); // brouillons / realises / rdv
  const [editingRdv, setEditingRdv] = useState(null);
  const [suiviParcours, setSuiviParcours] = useState(null);
  const [err, setErr] = useState("");

  const reload = async () => {
    setLoading(true);
    try {
      const [pList, rList] = await Promise.all([parcoursApi.list(), rdvApi.list()]);
      setParcours(pList); setRdvs(rList);
    } catch (e) { setErr(e?.response?.data?.detail || "Erreur de chargement"); }
    finally { setLoading(false); }
  };
  const [rdvLead, setRdvLead] = useState(null);
  useEffect(() => {
    reload();
    const l = sessionStorage.getItem("auprea_lead_rdv");
    if (l) { sessionStorage.removeItem("auprea_lead_rdv"); setRdvLead(JSON.parse(l)); setEditingRdv({}); }
  }, []);

  const brouillons = parcours.filter(p => p.statut === "brouillon");
  const realises   = parcours.filter(p => p.statut === "realise");
  const rdvFuturs  = rdvs.filter(r => r.statut === "planifie").sort((a, b) => (a.date + a.heure).localeCompare(b.date + b.heure));

  const demarrer = async (rid) => {
    try {
      const p = await rdvApi.demarrer(rid);
      onOpenParcours(p.id);
    } catch (e) { setErr(e?.response?.data?.detail || "Erreur"); }
  };

  const supprimer = async (id) => {
    if (!window.confirm("Supprimer ce parcours ?")) return;
    await parcoursApi.remove(id);
    reload();
  };

  const supprimerRdv = async (id) => {
    if (!window.confirm("Supprimer ce rendez-vous ?")) return;
    await rdvApi.remove(id);
    reload();
  };

  if (loading) return <Spinner />;

  return (
    <>
      {err && <Alert type="error"><span>⚠</span><span>{err}</span></Alert>}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display', serif" }}>Mes parcours & rendez-vous</div>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn onClick={() => { setRdvLead(null); setEditingRdv({}); }} data-testid="rdv-add">📅 Nouveau rendez-vous</Btn>
          <Btn variant="gold" onClick={onNouveau} data-testid="parcours-nouveau">+ Nouveau parcours client</Btn>
        </div>
      </div>

      <div style={{ display: "flex", gap: 4, borderBottom: `.5px solid ${C.border}`, marginBottom: 20 }}>
        {[
          { key: "brouillons", label: `Parcours en cours (${brouillons.length})` },
          { key: "realises",   label: `Bilans réalisés (${realises.length})` },
          { key: "rdv",        label: `Mes rendez-vous (${rdvFuturs.length})` },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`plist-tab-${t.key}`}
            style={{ background: "transparent", border: "none", cursor: "pointer",
              padding: "10px 16px", fontSize: 13, fontWeight: tab === t.key ? 700 : 500,
              color: tab === t.key ? C.navy : C.mid,
              borderBottom: tab === t.key ? `2px solid ${C.gold}` : "2px solid transparent",
              fontFamily: "inherit" }}>{t.label}</button>
        ))}
      </div>

      {tab === "brouillons" && (
        brouillons.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: C.muted, background: "#fff", borderRadius: 12, border: `.5px solid ${C.border}` }}>
            Aucun brouillon. Cliquez sur <strong>+ Nouveau parcours client</strong> pour commencer.
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 14 }}>
            {brouillons.map(p => {
              const ti = typeInfo(p.type_parcours);
              return (
                <div key={p.id} style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, padding: 18, borderLeft: `4px solid ${ti.couleur}` }} data-testid={`brouillon-${p.id}`}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                    <div style={{ fontSize: 10, color: ti.couleur, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em" }}>{ti.titre}</div>
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.navy, marginBottom: 4, fontFamily: "'Playfair Display', serif" }}>{p.fiche?.prenom} {p.fiche?.nom}</div>
                  <div style={{ fontSize: 11, color: C.mid, marginBottom: 12 }}>{p.fiche?.email || "—"} · Créé le {fmtDate(p.date_creation)}</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <Btn small variant="gold" onClick={() => onOpenParcours(p.id)} data-testid={`brouillon-open-${p.id}`}>Reprendre</Btn>
                    <Btn small variant="red" onClick={() => supprimer(p.id)} data-testid={`brouillon-del-${p.id}`}>Suppr.</Btn>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {tab === "realises" && (
        realises.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: C.muted, background: "#fff", borderRadius: 12, border: `.5px solid ${C.border}` }}>Aucun bilan finalisé pour le moment.</div>
        ) : (
          <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead><tr style={{ background: C.gray2 }}>
                <th style={{ textAlign: "left", padding: "10px 14px", fontSize: 10, color: C.muted, textTransform: "uppercase", fontWeight: 700 }}>Client</th>
                <th style={{ textAlign: "left", padding: "10px 14px", fontSize: 10, color: C.muted, textTransform: "uppercase", fontWeight: 700 }}>Parcours</th>
                <th style={{ textAlign: "left", padding: "10px 14px", fontSize: 10, color: C.muted, textTransform: "uppercase", fontWeight: 700 }}>Réalisé le</th>
                <th style={{ textAlign: "left", padding: "10px 14px", fontSize: 10, color: C.muted, textTransform: "uppercase", fontWeight: 700 }}>Suivi</th>
                <th style={{ textAlign: "right", padding: "10px 14px", fontSize: 10, color: C.muted, textTransform: "uppercase", fontWeight: 700 }}>Actions</th>
              </tr></thead>
              <tbody>
                {realises.map(p => {
                  const ti = typeInfo(p.type_parcours);
                  const ouverte = !!p.date_ouverture;
                  const paid = p.payment_status === "paid";
                  const term = p.relances_terminees;
                  return (
                    <tr key={p.id} style={{ borderTop: `.5px solid ${C.border}` }} data-testid={`realise-${p.id}`}>
                      <td style={{ padding: "12px 14px", fontWeight: 600, color: C.navy }}>{p.fiche?.prenom} {p.fiche?.nom}</td>
                      <td style={{ padding: "12px 14px" }}><span style={{ color: ti.couleur, fontWeight: 600 }}>{ti.titre}</span></td>
                      <td style={{ padding: "12px 14px", color: C.mid }}>{fmtDate(p.date_realisation || p.date_maj)}</td>
                      <td style={{ padding: "12px 14px" }}>
                        {paid ? (
                          <span style={{ background: C.greenlt, color: C.green, padding: "3px 8px", borderRadius: 20, fontSize: 10, fontWeight: 700 }}>✓ PAYÉE</span>
                        ) : term ? (
                          <span style={{ background: C.redlt, color: C.red, padding: "3px 8px", borderRadius: 20, fontSize: 10, fontWeight: 700 }}>À RAPPELER</span>
                        ) : p.public_token ? (
                          <span style={{ background: ouverte ? C.teallt : C.gray2, color: ouverte ? C.teal : C.muted, padding: "3px 8px", borderRadius: 20, fontSize: 10, fontWeight: 700 }}>
                            {ouverte ? "OUVERTE" : "NON OUVERTE"}
                          </span>
                        ) : <span style={{ fontSize: 10, color: C.muted }}>Non publiée</span>}
                      </td>
                      <td style={{ padding: "12px 14px", textAlign: "right", whiteSpace: "nowrap" }}>
                        <Btn small onClick={() => onOpenParcours(p.id)} data-testid={`realise-open-${p.id}`}>Voir</Btn>{" "}
                        {p.public_token && <Btn small variant="gold" onClick={() => setSuiviParcours(p)} data-testid={`realise-suivi-${p.id}`}>Suivi</Btn>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {tab === "rdv" && (
        rdvFuturs.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: C.muted, background: "#fff", borderRadius: 12, border: `.5px solid ${C.border}` }}>
            Aucun rendez-vous planifié. Cliquez sur <strong>📅 Nouveau rendez-vous</strong>.
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 14 }}>
            {rdvFuturs.map(r => {
              const ti = typeInfo(r.type_parcours);
              return (
                <div key={r.id} style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, padding: 18, borderLeft: `4px solid ${ti.couleur}` }} data-testid={`rdv-${r.id}`}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display', serif" }}>{fmtDate(r.date)}</div>
                    {r.heure && <span style={{ fontSize: 13, color: C.mid }}>· {r.heure}</span>}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.navy }}>{r.client_prenom} {r.client_nom}</div>
                  <div style={{ fontSize: 11, color: C.mid, marginBottom: 8 }}>{r.client_email || r.client_telephone || "—"}</div>
                  <div style={{ fontSize: 11, color: ti.couleur, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 12 }}>{ti.titre}</div>
                  {r.notes && <div style={{ fontSize: 12, color: C.mid, marginBottom: 12, fontStyle: "italic" }}>{r.notes}</div>}
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <Btn small variant="gold" onClick={() => demarrer(r.id)} data-testid={`rdv-start-${r.id}`}>▶ Démarrer le bilan</Btn>
                    <Btn small onClick={() => setEditingRdv(r)} data-testid={`rdv-edit-${r.id}`}>Modifier</Btn>
                    {r.client_telephone && <a href={waLink(r, user)} target="_blank" rel="noopener noreferrer" data-testid={`rdv-whatsapp-${r.id}`} style={{ textDecoration: "none" }}><Btn small variant="teal">WhatsApp</Btn></a>}
                    <Btn small variant="red" onClick={() => supprimerRdv(r.id)} data-testid={`rdv-del-${r.id}`}>Suppr.</Btn>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {editingRdv && <RdvModal user={user} initial={editingRdv.id ? editingRdv : null} initialLead={editingRdv.id ? null : rdvLead} onClose={() => { setEditingRdv(null); setRdvLead(null); }} onSaved={() => { reload(); setTab("rdv"); }} />}
      {suiviParcours && <SuiviModal parcours={suiviParcours} onClose={() => { setSuiviParcours(null); reload(); }} />}
    </>
  );
};
